import React from 'react';
import { oneOfType, node, string, bool } from 'prop-types';
import { propTypes } from '../../util/types';
import { userDisplayNameAsString } from '../../util/data';
import { Avatar } from '../../components';
import classNames from 'classnames';

import css from './HorizontalUserCard.module.css';

const HorizontalUserCard = props => {
  const { rootClassName, className, user, textContent, children, showEmail = false } = props;
  const classes = classNames(rootClassName || css.root, className);

  const displayName = userDisplayNameAsString(user);
  
  // Only show email if explicitly requested (for pending invites) or if user doesn't have a type (non-existing user)
  const shouldShowEmail = showEmail || !user.type;

  return (
    <div className={classes}>
      <div className={css.userInfo}>
        <Avatar className={css.userAvatar} user={user} disableProfileLink />
        <div className={css.userText}>
          <span className={css.userDisplayName}>{displayName}</span>
          <div className={css.userAttributes}>
            {shouldShowEmail && user.attributes?.email ? (
              <span className={css.userEmail}>{user.attributes.email}</span>
            ) : null}
            {textContent ? (
              <>
                {shouldShowEmail && user.attributes?.email ? (
                  <span className={css.dotDivider}>·</span>
                ) : null}
                {textContent}
              </>
            ) : null}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

HorizontalUserCard.defaultProps = {
  rootClassName: null,
  classNames: null,

  user: null,
};

HorizontalUserCard.propTypes = {
  rootClassName: string,
  classNames: string,

  user: propTypes.user,
  textContent: oneOfType([string, node]),
  children: node,
  showEmail: bool,
};

/**
 * Enhances a user object with email and profile attributes.
 *
 * @param {Object} user - The original user object to enhance.
 * @param {string} userEmail - The email address to add to the user object.
 * @returns {Object} - A new user object with updated attributes and profile.
 */
const enhanceUserWithProfile = (user, userEmail) => ({
  ...user,
  attributes: {
    ...user.attributes,
    email: userEmail,
    profile: {
      abbreviatedName: userEmail ? userEmail[0].toUpperCase() : '',
    },
  },
});

export const HorizontalNonExistingUserCard = props => {
  const { rootClassName, className, user, textContent, children } = props;
  const classes = classNames(rootClassName || css.root, className);

  const userEmail = user.email ? user.email : user.attributes?.email;
  const firstLetter = userEmail ? userEmail.charAt(0).toUpperCase() : '?';

  return (
    <div className={classes}>
      <div className={css.userInfo}>
        <div className={css.userAvatar}>
          <div className={css.letterAvatar}>
            {firstLetter}
          </div>
        </div>
        <div className={css.userText}>
          <span className={css.userDisplayName}>{userEmail}</span>
          {textContent ? <div className={css.userAttributes}>{textContent}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
};

export default HorizontalUserCard;
